<template>
  <div class="about">
    <div v-if="!isMobileApp" class="divider"></div>
    <div v-if="!isMobileApp" class="about__menu">
      <Menu />
      <div class="divider"></div>
    </div>
    <Intro
      :image="$root.isMobile ? aboutCoverMobile : aboutCover"
      :imagePadding="false"
      :imageAsBackground="true"
      :titleAbove="introTitleAbove"
      :title="introTitle"
      :subtitle="introSubtitle"
      :invertTextColor="introInvertTextColor"
    ></Intro>
    <div v-if="showIntroBottom" class="divider"></div>

    <div v-if="showIntroBottom" class="banner-wrapper">
      <div v-if="$root.isMobile" class="banner">
        <router-link to="/calendar"><img :src="calendarData && calendarData.calendarImages ? calendarData.calendarImages.mobile : ''"></router-link>
      </div>
      <div v-if="!$root.isMobile" class="banner">
        <router-link to="/calendar"><img :src="calendarData && calendarData.calendarImages ? calendarData.calendarImages.desktop : ''"></router-link>
      </div>
    </div>

    <TextBlock
      v-if="showTextBlock && textBlockItems.length > 0"
      :items="textBlockItems"
      id="textBlock"
    />

    <div class="divider"></div>
    <Goal
      v-if="showGoal && (goalEnabled || goalStepsEnabled)"
      :formatTotalKm="goalStepsEnabled ? formatTotalKmSteps : formatTotalKm"
      :formatCurrentKm="goalStepsEnabled ? formatCurrentKmSteps : formatCurrentKm"
      :totalKm="goalStepsEnabled ? goalSteps.necessaryDistance : goal.necessaryDistance"
      :currentKm="goalStepsEnabled ? goalSteps.completedDistance : goal.completedDistance"
      :currentMoney="goal && goalShowCharity ? `${goal.completedAmount} ₽` : ''"
      :localizedText="{
        distance: 'вместе преодолено', total: 'Вместе преодолеть:', amount: 'собрано во благо',
      }"
      fullWidth
      @distance-question="onShowDistance()"
      @money-question="onShowCharity()"
    ></Goal>

    <Ambassador
      v-if="ambassadorItems.length > 0"
      :type="ambassadorsType || 'general'"
      :title="ambassadorsTitle"
      :items="ambassadorsValues"
    ></Ambassador>

    <div v-if="showDummyBanner" class="banner-wrapper">
      <div v-if="showDummyBanner && $root.isMobile" class="banner">
        <img :src="dummyBannerMobile" />
      </div>
      <div v-if="showDummyBanner && !$root.isMobile" class="banner">
        <img :src="dummyBanner" />
      </div>
    </div>

    <div class="nominations">
      <div class="nominations__slider">
        <Slider
          v-if="nominationsActive ? nominationsActive.total > 0 : false"
          cols="1"
          :slidesPerView="1"
          :loop="true"
          :navigationOffset="nominationsNavigationOffset"
          :paginationOffset="nominationsPaginationOffset"
          class="nominations-slider"
        >
          <NominationCard
            v-for="item in nominationsActive.values"
            :key="item.nominationId"
            type="personal"
            state="in-progress"
            :image="getNominationImage(item)"
            :nominationText="getNominationDates(item)"
            :lockedText="{ text: 'Для участия необходимо', link: 'подключить трекер' }"
            :title="item.title"
            :rules="{ text: 'Правила', action: () => onShowRules(item) }"
            :prizes="{ text: 'Награды', action: () => onShowPrizes(item) }"
            :ratingText="{ general: 'Текущий рейтинг', personal: 'Вы в рейтинге' }"
            :values="getNominationRating(item)"
            @show-details="onShowRating(item)"
            @locked-click="onShowOnboarding()"
          ></NominationCard>
        </Slider>
      </div>
    </div>

    <TopCurators v-if="themeDone && showTopCurators"></TopCurators>
    <div id="involvement-rating"></div>

    <Partners
      v-if="showPartners"
      title="Партнёры и спонсоры"
      :items="partnersValues"
      fullWidth
    ></Partners>
    <div v-if="showPartners"  class="divider"></div>

    <Intro
      class="intro-bottom"
      v-if="showIntroBottom"
      :image="$root.isMobile ? aboutBottomImageMobile : aboutBottomImageDesktop"
      :imagePadding="false"
      :imageAsBackground="true"
      :title="introBottomTitle"
      :subtitle="introBottomSubtitle"
      :invertTextColor="introBottomInvertTextColor"
      :buttonText="introBottomButtonText"
      :buttonAction="{ action: onLogin }"
    ></Intro>

    <RulesPrizesModal
      :active="rulesPrizesModal"
      :image="rulesPrizesImage"
      :title="rulesPrizesTitle"
      :subtitle="rulesPrizesSubtitle"
      :nominationId="rulesPrizesNominationId"
      state="personal"
      :tabs="rulesPrizesTabs"
      :activeTab="rulesPrizesTabActive"
      :isMobileApp="isMobileApp"
      @close="rulesPrizesModal = false"
    />

    <DistanceCharityModal
      :active="distanceCharityModal"
      :type="distanceCharityType"
      :localizedText="distanceCharityLocalizedText"
      moreLink=""
      @close="distanceCharityModal = false"
    />

  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import dayjs from 'dayjs';
import Intro from '@sc-component-library/intro/src/main';
import Goal from '@sc-component-library/goal/src/main';
import Ambassador from '@sc-component-library/ambassador/src/main';
import NominationCard from '@sc-component-library/nomination-card/src/main';
import Partners from '@sc-component-library/partners/src/main';
import Menu from '@/components/Menu/Menu.vue';
import TopCurators from '@/components/TopCurators/TopCurators.vue';
import TextBlock from '@/components/TextBlock/TextBlock.vue';
import RulesPrizesModal from '@/components/RulesPrizesModal/RulesPrizesModal.vue';
import DistanceCharityModal from '@/components/DistanceCharityModal/DistanceCharityModal.vue';
import {
  scrollToId, formatDateRange, addNonBreakingSpaces, formatNumber, formatSingleDate, formatDateRangeIntro,
} from '@/assets/js/helpers';

export default {
  name: 'About',
  components: {
    Intro,
    Goal,
    Partners,
    Ambassador,
    NominationCard,
    Menu,
    TopCurators,
    TextBlock,
    RulesPrizesModal,
    DistanceCharityModal,
  },
  data() {
    return {
      distanceCharityModal: false,
      distanceCharityType: '',
      distanceCharityLocalizedText: {
        title: '',
        subtitle: '',
      },
      rulesPrizesModal: false,
      rulesPrizesImage: '',
      rulesPrizesTitle: '',
      rulesPrizesSubtitle: '',
      rulesPrizesNominationId: '',
      rulesPrizesTabs: [
        {
          title: 'Правила',
          enabled: true,
          content: '',
        },
        {
          title: 'Награды',
          enabled: true,
          content: '',
        },
        {
          title: 'Рейтинг',
          enabled: true,
        },
      ],
      rulesPrizesTabActive: 0,
      nominations: [],
      nominationsState: 'in-progress',
      goalEnabled: false,
      goalStepsEnabled: false,
      totalDistance: 0,
      completedDistance: 0,
      contributionPercentage: 0,
      contributionPercentageSteps: 0,
      totalDistanceSteps: 0,
      completedDistanceSteps: 0,
      textBlockItems: [],
      ambassadorItems: [],
      calendarData: [],
    };
  },
  computed: {
    ...mapGetters('settings', ['theme', 'themeDone', 'options', 'resources']),
    ...mapGetters('goals', ['goal', 'goalSteps']),
    ...mapGetters('nominations', ['nominationsActive']),
    ...mapGetters('trackers', ['trackers']),
    ...mapGetters('content', ['onboardingItems', 'contentStatus', 'ambassadors', 'introSections', 'partners']),
    ...mapGetters('user', ['isMobileApp']),
    ...mapGetters('calendar', ['calendar']),
    showPartners() {
      return Array.isArray(this.partners) && this.partners.length > 0;
    },
    partnersValues() {
      if (!this.partners || !Array.isArray(this.partners)) return [];
      return this.partners.map((partner) => ({
        image: partner.logo || '',
        link: partner.link || '',
      }));
    },
    showTopCurators() {
      return this.options ? this.options.showTopCurators === true : false;
    },
    showIntroBottom() {
      if (!this.introSections) return false;
      const title = this.introSections.aboutProgramTitle || '';
      const subtitle = this.introSections.aboutProgramShortDescription || '';
      const buttonText = this.introSections.aboutProgramButtonText || '';
      // Показываем секцию, если хотя бы одно из полей заполнено
      return !!(title || subtitle || buttonText);
    },
    introImageAsBackground() {
      return this.options ? this.options.introImageAsBackground === true : false;
    },
    introInvertTextColor() {
      return this.introSections ? this.introSections.topSectionButtonInversion === true : false;
    },
    introBottomInvertTextColor() {
      return this.introSections ? this.introSections.aboutProgramButtonInversion === true : false;
    },
    homeSections() {
      return this.options ? this.options.homeSections || null : null;
    },
    ambassadorsType() {
      return this.options ? this.options.ambassadorsType || '' : 'general';
    },
    goalShowCharity() {
      return this.options ? this.options.goalShowCharity === true : false;
    },
    showGoal() {
      return this.options && this.options.showGoal !== undefined ? this.options.showGoal === true : true;
    },
    aboutCover() {
      return this.introSections ? this.introSections.aboutPageDesktopImageUrl || '' : '';
    },
    aboutBottomCover() {
      return this.introSections ? this.introSections.aboutPageDesktopImageUrl || '' : '';
    },
    aboutCoverMobile() {
      return this.introSections ? this.introSections.aboutPageMobileImageUrl || '' : '';
    },
    aboutBottomImageMobile() {
      return this.introSections ? this.introSections.aboutProgramMobileImageHash || '' : '';
    },
    aboutBottomImageDesktop() {
      return this.introSections ? this.introSections.aboutProgramDesktopImageHash || '' : '';
    },
    introTitleAbove() {
      if (!this.introSections) return '';
      const datesString = this.introSections.topSectionDates || '';
      if (!datesString) return '';

      const dateRanges = datesString.split(' - ');
      if (dateRanges.length === 1) {
        return formatSingleDate(dateRanges[0]);
      } if (dateRanges.length === 2) {
        return formatDateRangeIntro(dateRanges[0], dateRanges[1]);
      }
      return '';
    },
    introTitle() {
      return this.introSections ? this.introSections.topSectionTitle || '' : '';
    },
    introBottomTitle() {
      return this.introSections ? this.introSections.aboutProgramTitle || '' : '';
    },
    introSubtitle() {
      return this.introSections ? this.introSections.topSectionShortDescription || '' : '';
    },
    introBottomSubtitle() {
      return this.introSections ? this.introSections.aboutProgramShortDescription || '' : '';
    },
    introButtonText() {
      return this.resources ? this.resources.introButtonText || '' : '';
    },
    introBottomButtonText() {
      return this.introSections ? this.introSections.aboutProgramButtonText || '' : '';
    },
    introNote() {
      return this.resources ? this.resources.introNote || '' : '';
    },
    ambassadorsTitle() {
      return this.resources ? this.resources.ambassadorsTitle || this.ambassadorsTitleDefault : this.ambassadorsTitleDefault;
    },
    ambassadorsTitleDefault() {
      return addNonBreakingSpaces('Вас будут поддерживать и мотивировать');
    },
    dummyBanner() {
      return this.resources ? this.resources.dummyBanner || '' : '';
    },
    dummyBannerMobile() {
      return this.resources ? this.resources.dummyBannerMobile || '' : '';
    },
    goalModalSubtitle() {
      return this.resources ? this.resources.goalModalSubtitle || '' : '';
    },
    goalModalCharityTitle() {
      return this.resources ? this.resources.goalModalCharityTitle || '' : '';
    },
    goalModalCharitySubtitle() {
      return this.resources ? this.resources.goalModalCharitySubtitle || '' : '';
    },
    formatTotalKm() {
      return this.goal ? formatNumber(this.goal.necessaryDistance, 'км') : '0 км';
    },
    formatCurrentKm() {
      return this.goal ? formatNumber(this.goal.completedDistance, 'км') : '0 км';
    },
    formatTotalKmSteps() {
      return this.goalSteps ? formatNumber(this.goalSteps.necessaryDistance, 'км') : '0 км';
    },
    formatCurrentKmSteps() {
      return this.goalSteps ? formatNumber(this.goalSteps.completedDistance, 'км') : '0 км';
    },
    nominationsNavigationOffset() {
      return {
        default: '0', md: '72', lg: '72', xl: '84',
      };
    },
    nominationsPaginationOffset() {
      return {
        default: '28', md: '44', lg: '44', xl: '60',
      };
    },
    showTextBlock() {
      return this.homeSections ? this.homeSections.textBlock !== undefined : false;
    },
    showDummyBanner() {
      return this.homeSections ? this.homeSections.dummyBanner !== undefined : false;
    },
    ambassadorsValues() {
      return this.ambassadors ? this.updatedAmbassadors() || [] : [];
    },
    isFinished() {
      return this.options ? this.options.isFinished === true : false;
    },
  },
  methods: {
    ...mapActions('user', ['getProfile']),
    ...mapActions('wallet', ['getBalance']),
    ...mapActions('goals', ['getGoal', 'getGoalSteps']),
    ...mapActions('nominations', ['getNominationsActive']),
    ...mapActions('trackers', ['getTrackers']),
    ...mapActions('content', ['getOnboardingItems', 'getAmbassadors', 'getPartners']),
    ...mapActions('calendar', ['getCalendar']),
    updatedAmbassadors() {
      return this.ambassadors.map((ambassador) => ({
        name: ambassador.fullName,
        avatar: ambassador.desktopImageUrl,
        avatarMobile: ambassador.mobileImageUrl,
        taskText: ambassador.content,
        quote: ambassador.description,
      }));
    },
    onLogin() {
      window.open('https://vk.com/runningmr', '_blank');
    },
    onShowDistance() {
      this.distanceCharityType = 'distance';
      const subtitleDefault = addNonBreakingSpaces('Бегайте и ходите. Внесите свой вклад в достижение общей цели.');
      this.distanceCharityLocalizedText = {
        title: 'Вместе преодолеть',
        subtitle: this.goalModalSubtitle ? this.goalModalSubtitle : subtitleDefault,
      };
      this.distanceCharityModal = true;
    },
    onShowCharity() {
      this.distanceCharityType = 'charity';
      this.distanceCharityLocalizedText = {
        title: this.goalModalCharityTitle,
        subtitle: this.goalModalCharitySubtitle,
      };
      this.distanceCharityModal = true;
    },
    onShowRules(item) {
      this.rulesPrizesTabs[0].content = item.rules || '';
      this.rulesPrizesTabs[1].content = item.awards || '';
      this.rulesPrizesTabs[2].enabled = false;
      this.rulesPrizesTabActive = 0;
      this.rulesPrizesImage = this.getNominationImage(item);
      this.rulesPrizesTitle = this.getNominationDates(item);
      this.rulesPrizesSubtitle = item.title;
      this.rulesPrizesModal = true;
    },
    onShowPrizes(item) {
      this.rulesPrizesTabs[0].content = item.rules || '';
      this.rulesPrizesTabs[1].content = item.awards || '';
      this.rulesPrizesTabs[2].enabled = false;
      this.rulesPrizesTabActive = 1;
      this.rulesPrizesImage = this.getNominationImage(item);
      this.rulesPrizesTitle = this.getNominationDates(item);
      this.rulesPrizesSubtitle = item.title;
      this.rulesPrizesModal = true;
    },
    onShowRating(item) {
      this.rulesPrizesTabs[0].content = item.rules || '';
      this.rulesPrizesTabs[1].content = item.awards || '';
      this.rulesPrizesTabs[2].enabled = true;
      this.rulesPrizesTabActive = 2;
      this.rulesPrizesImage = this.getNominationImage(item);
      this.rulesPrizesTitle = this.getNominationDates(item);
      this.rulesPrizesSubtitle = item.title;
      this.rulesPrizesNominationId = item.nominationId;
      this.rulesPrizesModal = true;
    },
    onShowOnboarding() {
      this.$root.showOnboarding({ step: 'choose' });
    },
    getNominationImage(item) {
      if (!item || !item.images) return '';
      return this.$root.isDesktop ? item.images.desktop || '' : item.images.mobile || '';
    },
    getNominationDates(item) {
      if (!item) return '';
      const startDate = dayjs.utc(item.startDate);
      const finishDate = dayjs.utc(item.finishDate);
      if (startDate.isValid() && finishDate.isValid()) {
        return formatDateRange({ start: startDate, finish: finishDate, showYear: true });
      }
      return '';
    },
    getNominationRating(item) {
      if (!item) return [];
      const rating = [];
      for (let i = 0; i < item.leaders.length; i += 1) {
        const it = {
          number: item.leaders[i].position.toString(),
          name: `${item.leaders[i].lastName} ${item.leaders[i].firstName}`,
          value: item.leaders[i].points.toString(),
          current: item.leaders[i].isCurrentUser === true,
        };
        rating.push(it);
      }
      return rating;
    },
    setNominationsState() {
      if (this.trackers.filter((i) => i.state === 'Connected').length > 0) {
        this.nominationsState = 'in-progress';
      } else {
        this.nominationsState = 'locked';
      }
    },
    setTextBlock() {
      if (this.showTextBlock) {
        this.getOnboardingItems()
          .then(() => {
            if (this.contentStatus === 200) {
              this.textBlockItems = this.onboardingItems || [];
            }
          });
      }
    },
    setAmbassadorBlock() {
      this.getAmbassadors()
        .then(() => {
          if (this.contentStatus === 200) {
            this.ambassadorItems = this.ambassadors || [];
          }
        });
    },
  },
  mounted() {
    this.getProfile();
    this.getBalance();
    this.setTextBlock();
    this.setAmbassadorBlock();
    this.getPartners();
    this.getCalendar()
      .then(() => {
        if (this.calendar) {
          this.calendarData = this.calendar ? this.calendar : [];
        }
      });
    this.getGoal()
      .then(() => {
        if (this.goal) {
          this.goalEnabled = this.goal.isPrimaryGoalEnabled ? this.goal.isPrimaryGoalEnabled : false;
          this.contributionPercentage = Math.round(this.goal.contribution.contributionPercentage);
          this.totalDistance = this.goal.contribution.totalDistance;
          this.completedDistance = this.goal.completedDistance;
        }
      });
    this.getGoalSteps()
      .then(() => {
        if (this.goalSteps) {
          this.goalStepsEnabled = this.goalSteps.isPrimaryGoalEnabled ? this.goalSteps.isPrimaryGoalEnabled : false;
          this.contributionPercentageSteps = Math.round(this.goalSteps.contribution.contributionPercentage);
          this.totalDistanceSteps = formatNumber(this.goalSteps.contribution.totalDistance, '<span>км</span>');
          this.completedDistanceSteps = formatNumber(this.goalSteps.completedDistance, '<span>км</span>');
        }
      });
    this.getTrackers()
      .then(() => {
        if (this.trackers) {
          const connected = this.trackers.filter((i) => i.state === 'Connected');
          if (connected.length > 0) {
            localStorage.removeItem('returnFromGuide');
          }
          this.getNominationsActive({ offset: 0, count: 12 })
            .then(() => {
              this.setNominationsState();
              this.nominations = this.nominationsActive ? this.nominationsActive.values || [] : [];
              setTimeout(() => {
                if (window.location.hash) {
                  const id = window.location.hash.length > 1 ? window.location.hash.slice(1) : '';
                  if (id !== '') scrollToId(id);
                }
              }, 1000);
            });
        }
      });
  },
};
</script>

<style lang="scss" scoped>
  @import "./About";
</style>
